/**
 * A list of all model APIs natively supported by ChainForge.
 */
import Bottleneck from "bottleneck";
import { UserForcedPrematureExit } from "./errors";
import type { LLMGroup, LLMSpec } from "./typing";

export enum NativeLLM {
  // WebLLM (fully in-browser, no API key). Model IDs come from web-llm's own
  // prebuilt list; these are the ones small enough to load on a normal laptop,
  // with their download sizes in the comments.
  WebLLM_Gemma3_1B = "gemma3-1b-it-q4f16_1-MLC", // 711 MB
  WebLLM_Llama3_2_1B = "Llama-3.2-1B-Instruct-q4f16_1-MLC", // 879 MB
  WebLLM_Qwen2_5_0_5B = "Qwen2.5-0.5B-Instruct-q4f16_1-MLC", // 945 MB
  WebLLM_Qwen3_5_0_8B = "Qwen3.5-0.8B-q4f16_1-MLC", // 1629 MB
  WebLLM_SmolLM2_1_7B = "SmolLM2-1.7B-Instruct-q4f16_1-MLC", // 1774 MB
  WebLLM_Qwen3_1_7B = "Qwen3-1.7B-q4f16_1-MLC", // 2037 MB

  // OpenAI Chat. Models OpenAI still serves, newest first.
  // See https://developers.openai.com/api/docs/models
  OpenAI_GPT6_Astra = "gpt-6-astra",
  OpenAI_GPT6_1_Sol = "gpt-6.1-sol",
  OpenAI_GPT6_Sol = "gpt-6-sol",
  OpenAI_GPT6_Luna = "gpt-6-luna",
  OpenAI_GPT5_6_Sol = "gpt-5.6-sol",
  OpenAI_GPT5_6 = "gpt-5.6", // alias of gpt-5.6-sol
  OpenAI_GPT5_6_Terra = "gpt-5.6-terra",
  OpenAI_GPT5_6_Luna = "gpt-5.6-luna",
  OpenAI_GPT5_6_Cyber = "gpt-5.6-cyber",
  OpenAI_GPT5_5 = "gpt-5.5",
  OpenAI_GPT5_5_Pro = "gpt-5.5-pro",
  OpenAI_GPT5_4 = "gpt-5.4",
  OpenAI_GPT5_4_mini = "gpt-5.4-mini",
  OpenAI_GPT5_4_nano = "gpt-5.4-nano",
  OpenAI_GPT5_4_Pro = "gpt-5.4-pro",
  OpenAI_GPT5_2 = "gpt-5.2",
  OpenAI_GPT5_1 = "gpt-5.1",
  OpenAI_GPT5 = "gpt-5", // snapshot shuts down 2026-12-11
  OpenAI_GPT5_mini = "gpt-5-mini", // snapshot shuts down 2026-12-11
  OpenAI_GPT5_nano = "gpt-5-nano", // snapshot shuts down 2026-12-11
  OpenAI_GPT5_pro = "gpt-5-pro", // snapshot shuts down 2026-12-11
  OpenAI_GPT4_1 = "gpt-4.1",
  OpenAI_GPT4_1_mini = "gpt-4.1-mini",
  OpenAI_GPT4_1_nano = "gpt-4.1-nano", // shuts down 2026-10-23
  OpenAI_GPT4_O = "gpt-4o",
  OpenAI_GPT4_O_Mini = "gpt-4o-mini",
  OpenAI_GPT4_O_0513 = "gpt-4o-2024-05-13", // shuts down 2026-10-23
  OpenAI_o4_mini = "o4-mini",
  OpenAI_o3 = "o3", // snapshot shuts down 2026-12-11
  OpenAI_o3_pro = "o3-pro", // snapshot shuts down 2026-12-11
  OpenAI_o3_mini = "o3-mini", // shuts down 2026-10-23
  OpenAI_o1 = "o1", // snapshot shuts down 2026-12-11
  OpenAI_o1_pro = "o1-pro", // snapshot shuts down 2026-12-11
  OpenAI_GPT4_Turbo = "gpt-4-turbo", // shuts down 2026-10-23
  OpenAI_GPT4_Turbo_0409 = "gpt-4-turbo-2024-04-09", // shuts down 2026-10-23
  OpenAI_GPT4 = "gpt-4", // alias of gpt-4-0613; shuts down 2026-10-23
  OpenAI_GPT4_0613 = "gpt-4-0613", // shuts down 2026-10-23
  OpenAI_GPT4_1106_Prev = "gpt-4-1106-preview", // shuts down 2026-10-23
  OpenAI_ChatGPT = "gpt-3.5-turbo",
  OpenAI_ChatGPT_0125 = "gpt-3.5-turbo-0125",
  OpenAI_ChatGPT_1106 = "gpt-3.5-turbo-1106", // shuts down 2026-09-28

  // OpenAI chat models that have shut down. Kept so old flows still load.
  OpenAI_GPT5_Chat = "gpt-5-chat-latest", // shut down 2026-07-23
  OpenAO_ChatGPT4_O = "chatgpt-4o-latest", // shut down 2026-02-17
  OpenAI_GPT4_0125_Prev = "gpt-4-0125-preview", // shut down 2026-03-26
  OpenAI_GPT4_Turbo_Prev = "gpt-4-turbo-preview", // shut down 2026-03-26
  OpenAI_o1_mini = "o1-mini", // shut down 2025-10-27
  OpenAI_GPT4_5_Prev = "gpt-4.5-preview", // shut down 2025-07-14
  OpenAI_GPT4_32k = "gpt-4-32k", // shut down 2025-06-06
  OpenAI_GPT4_32k_0314 = "gpt-4-32k-0314", // shut down 2025-06-06
  OpenAI_GPT4_32k_0613 = "gpt-4-32k-0613", // shut down 2025-06-06
  OpenAI_GPT4_0314 = "gpt-4-0314", // shut down 2024-06-13
  OpenAI_GPT4_O_0806 = "gpt-4o-2024-08-06", // no longer listed by OpenAI
  OpenAI_ChatGPT_16k = "gpt-3.5-turbo-16k", // shut down 2024-09-13
  OpenAI_ChatGPT_16k_0613 = "gpt-3.5-turbo-16k-0613", // shut down 2024-09-13
  OpenAI_ChatGPT_0301 = "gpt-3.5-turbo-0301", // shut down 2024-09-13
  OpenAI_ChatGPT_0613 = "gpt-3.5-turbo-0613", // shut down 2024-09-13

  // OpenAI Text Completions
  OpenAI_ChatGPT_Instruct = "gpt-3.5-turbo-instruct", // shuts down 2026-09-28
  OpenAI_Davinci003 = "text-davinci-003", // shut down 2024-01-04
  OpenAI_Davinci002 = "text-davinci-002", // shut down 2024-01-04

  // OpenAI Image models
  OpenAI_GPT_Image_2_5_Flare = "gpt-image-2.5-flare",
  OpenAI_GPT_Image_2_5_Sunburst = "gpt-image-2.5-sunburst",
  OpenAI_GPT_Image_2 = "gpt-image-2",
  OpenAI_GPT_Image_1_5 = "gpt-image-1.5", // shuts down 2026-12-01
  OpenAI_GPT_Image_1 = "gpt-image-1", // retiring; replaced by gpt-image-2
  OpenAI_GPT_Image_1_mini = "gpt-image-1-mini", // shuts down 2026-12-01
  OpenAI_DallE_2 = "dall-e-2", // shut down 2026-05-12; kept so old flows still load
  OpenAI_DallE_3 = "dall-e-3", // shut down 2026-05-12

  // Azure OpenAI Endpoints
  Azure_OpenAI = "azure-openai",

  // Anthropic. Models Anthropic still serves, newest first.
  // See https://platform.claude.com/docs/en/about-claude/models/overview
  // NOTE: getProvider() routes anything starting with "claude" to Anthropic,
  // so models released after this list still work when typed in by hand.
  Claude_opus_5_5 = "claude-opus-5-5",
  Claude_sonnet_5_5 = "claude-sonnet-5-5",
  Claude_fable_5_1 = "claude-fable-5-1",
  Claude_fable_5 = "claude-fable-5",
  Claude_opus_5 = "claude-opus-5",
  Claude_opus_4_8 = "claude-opus-4-8",
  Claude_opus_4_7 = "claude-opus-4-7",
  Claude_opus_4_6 = "claude-opus-4-6",
  Claude_opus_4_5 = "claude-opus-4-5",
  Claude_opus_4 = "claude-opus-4-0", // deprecated
  Claude_sonnet_5 = "claude-sonnet-5",
  Claude_sonnet_4_6 = "claude-sonnet-4-6",
  Claude_sonnet_4_5 = "claude-sonnet-4-5",
  Claude_sonnet_4 = "claude-sonnet-4-0", // deprecated
  Claude_haiku_4_5 = "claude-haiku-4-5", // retires no sooner than 2026-10-15

  // Anthropic models that have been retired. Kept so old flows still load.
  Claude_opus_4_1 = "claude-opus-4-1", // retired 2026-08-05
  Claude_v3_haiku = "claude-3-haiku-20240307", // retired 2026-04-19
  Claude_v3_7_sonnet_latest = "claude-3-7-sonnet-latest", // retired 2026-02-19
  Claude_v3_7_sonnet_20250219 = "claude-3-7-sonnet-20250219", // retired 2026-02-19
  Claude_v3_5_haiku_latest = "claude-3-5-haiku-latest", // retired 2026-02-19
  Claude_v3_opus_latest = "claude-3-opus-latest", // retired 2026-01-05
  Claude_v3_opus = "claude-3-opus-20240229", // retired 2026-01-05
  Claude_v3_5_sonnet_latest = "claude-3-5-sonnet-latest", // retired 2025-10-28
  Claude_v3_5_sonnet = "claude-3-5-sonnet-20240620", // retired 2025-10-28
  Claude_v3_sonnet = "claude-3-sonnet-20240229", // retired 2025-07-21
  Claude_v2_1 = "claude-2.1", // retired 2025-07-21
  Claude_v2 = "claude-2", // retired 2025-07-21
  Claude_v2_0 = "claude-2.0", // retired 2025-07-21
  Claude_1_instant = "claude-instant-1",
  Claude_1_instant_1 = "claude-instant-1.1",
  Claude_1_instant_2 = "claude-instant-1.2",
  Claude_v1 = "claude-v1",
  Claude_v1_0 = "claude-v1.0",
  Claude_v1_2 = "claude-v1.2",
  Claude_v1_3 = "claude-v1.3",
  Claude_v1_instant = "claude-instant-v1",

  // Google Gemini 3 models
  // See https://ai.google.dev/gemini-api/docs/models
  GEMINI_v3_8_flash = "gemini-3.8-flash",
  GEMINI_v3_7_flash = "gemini-3.7-flash",
  GEMINI_v3_6_flash = "gemini-3.6-flash",
  GEMINI_v3_5_flash = "gemini-3.5-flash",
  GEMINI_v3_5_flash_lite = "gemini-3.5-flash-lite",
  GEMINI_v3_1_flash_lite = "gemini-3.1-flash-lite",
  GEMINI_v3_1_pro_preview = "gemini-3.1-pro-preview",
  GEMINI_v3_flash_preview = "gemini-3-flash-preview",

  // Google Gemini 2.5 models. Since 2026-09-18, only open to projects that used them before.
  GEMINI_v2_5_pro = "gemini-2.5-pro",
  GEMINI_v2_5_flash = "gemini-2.5-flash",
  GEMINI_v2_5_flash_lite = "gemini-2.5-flash-lite",

  // Google models that have been shut down. Kept so old flows still load.
  GEMINI_v2_flash = "gemini-2.0-flash", // shut down 2026-06-01
  GEMINI_v2_flash_lite = "gemini-2.0-flash-lite", // shut down 2026-06-01
  GEMINI_v1_5_flash = "gemini-1.5-flash", // shut down
  GEMINI_v1_5_flash_8B = "gemini-1.5-flash-8b", // shut down
  GEMINI_v1_5_pro = "gemini-1.5-pro", // shut down

  // Google image generation models
  GEMINI_v3_1_flash_image = "gemini-3.1-flash-image",
  GEMINI_v3_1_flash_lite_image = "gemini-3.1-flash-lite-image",
  GEMINI_v3_pro_image = "gemini-3-pro-image",
  GEMINI_v2_5_flash_image = "gemini-2.5-flash-image", // shuts down 2026-10-02

  // DeepSeek
  DeepSeek_Flash = "deepseek-flash",
  DeepSeek_V4_Pro = "deepseek-v4-pro",
  DeepSeek_Chat = "deepseek-chat",
  DeepSeek_Reasoner = "deepseek-reasoner",

  // MiniMax
  MiniMax_M3 = "MiniMax-M3",
  MiniMax_M2_7 = "MiniMax-M2.7",
  MiniMax_M2_7_highspeed = "MiniMax-M2.7-highspeed",

  // HuggingFace models from the retired Inference API. Serving moved to
  // Inference Providers, whose models carry the HUGGINGFACE_PREFIX below and so
  // do not need an entry here; these are kept only so old flows still resolve.
  HF_MISTRAL_7B_INSTRUCT = "mistralai/Mistral-7B-Instruct-v0.1",
  HF_ZEPHYR_7B_BETA = "HuggingFaceH4/zephyr-7b-beta",
  HF_FALCON_7B_INSTRUCT = "tiiuae/falcon-7b-instruct",
  HF_SANTACODER = "bigcode/santacoder",
  HF_STARCODER = "bigcode/starcoder",
  HF_DIALOGPT_LARGE = "microsoft/DialoGPT-large", // chat model
  HF_GPT2 = "gpt2",
  HF_BLOOM_560M = "bigscience/bloom-560m",
  // A special flag for a user-defined HuggingFace model endpoint.
  // The actual model name will be passed as a param to the LLM call function.
  HF_OTHER = "Other (HuggingFace)",

  Ollama = "ollama",
  // Ollama's decision models (e.g. nimble, tev1), which answer typed questions
  // rather than writing text. Kept apart from "ollama" so they have their own
  // settings, and are only offered where a decision model can be used.
  Ollama_Decision = "ollama-decision",

  // Together.ai
  Together_ZeroOneAI_01ai_Yi_Chat_34B = "together/zero-one-ai/Yi-34B-Chat",
  Together_AllenAI_OLMo_Instruct_7B = "together/allenai/OLMo-7B-Instruct",
  Together_AllenAI_OLMo_Twin2T_7B = "together/allenai/OLMo-7B-Twin-2T",
  Together_AllenAI_OLMo_7B = "together/allenai/OLMo-7B",
  Together_Austism_Chronos_Hermes_13B = "together/Austism/chronos-hermes-13b",
  Together_cognitivecomputations_Dolphin_2_5_Mixtral_8x7b = "together/cognitivecomputations/dolphin-2.5-mixtral-8x7b",
  Together_databricks_DBRX_Instruct = "together/databricks/dbrx-instruct",
  Together_DeepSeek_Deepseek_Coder_Instruct_33B = "together/deepseek-ai/deepseek-coder-33b-instruct",
  Together_DeepSeek_DeepSeek_LLM_Chat_67B = "together/deepseek-ai/deepseek-llm-67b-chat",
  Together_garagebAInd_Platypus2_Instruct_70B = "together/garage-bAInd/Platypus2-70B-instruct",
  Together_Google_Gemma_Instruct_2B = "together/google/gemma-2b-it",
  Together_Google_Gemma_Instruct_7B = "together/google/gemma-7b-it",
  Together_Gryphe_MythoMaxL2_13B = "together/Gryphe/MythoMax-L2-13b",
  Together_LMSys_Vicuna_v1_5_13B = "together/lmsys/vicuna-13b-v1.5",
  Together_LMSys_Vicuna_v1_5_7B = "together/lmsys/vicuna-7b-v1.5",
  Together_Meta_Code_Llama_Instruct_13B = "together/codellama/CodeLlama-13b-Instruct-hf",
  Together_Meta_Code_Llama_Instruct_34B = "together/codellama/CodeLlama-34b-Instruct-hf",
  Together_Meta_Code_Llama_Instruct_70B = "together/codellama/CodeLlama-70b-Instruct-hf",
  Together_Meta_Code_Llama_Instruct_7B = "together/codellama/CodeLlama-7b-Instruct-hf",
  Together_Meta_LLaMA2_Chat_70B = "together/meta-llama/Llama-2-70b-chat-hf",
  Together_Meta_LLaMA2_Chat_13B = "together/meta-llama/Llama-2-13b-chat-hf",
  Together_Meta_LLaMA2_Chat_7B = "together/meta-llama/Llama-2-7b-chat-hf",
  Together_Meta_LLaMA3_Chat_8B = "together/meta-llama/Llama-3-8b-chat-hf",
  Together_Meta_LLaMA3_Chat_70B = "together/meta-llama/Llama-3-70b-chat-hf",
  Together_Meta_LLaMA3_3_70B = "meta-llama/Llama-3.3-70B-Instruct-Turbo",
  Together_Meta_LLaMA3_1_8B = "meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo",
  Together_Meta_LLaMA3_1_70B = "meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo",
  Together_Meta_LLaMA3_1_405B = "meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo",
  Together_Meta_LLaMA3_8B = "meta-llama/Meta-Llama-3-8B-Instruct-Turbo",
  Together_Meta_LLaMA3_70B = "meta-llama/Meta-Llama-3-70B-Instruct-Turbo",
  Together_Meta_LLaMA3_2_3B = "meta-llama/Llama-3.2-3B-Instruct-Turbo",
  Together_Meta_LLaMA3_8B_Lite = "meta-llama/Meta-Llama-3-8B-Instruct-Lite",
  Together_Meta_LLaMA3_70B_Lite = "meta-llama/Meta-Llama-3-70B-Instruct-Lite",
  Together_Nvidia_LLaMA3_1_Nemotron_70B = "nvidia/Llama-3.1-Nemotron-70B-Instruct-HF",
  Together_Qwen_Qwen2_5_Coder_32B = "Qwen/Qwen2.5-Coder-32B-Instruct",
  Together_Qwen_QwQ_32B_Preview = "Qwen/QwQ-32B-Preview",
  Together_Microsoft_WizardLM_2_8x22B = "microsoft/WizardLM-2-8x22B",
  Together_Google_Gemma2_27B = "google/gemma-2-27b-it",
  Together_Google_Gemma2_9B = "google/gemma-2-9b-it",
  Together_DeepSeek_3 = "deepseek-ai/DeepSeek-V3",
  Together_DeepSeek_R1 = "deepseek-ai/DeepSeek-R1",
  Together_mistralai_Mistral_7B_Instruct_v0_3 = "mistralai/Mistral-7B-Instruct-v0.3",
  Together_Qwen_Qwen2_5_7B_Turbo = "Qwen/Qwen2.5-7B-Instruct-Turbo",
  Together_Qwen_Qwen2_5_72B_Turbo = "Qwen/Qwen2.5-72B-Instruct-Turbo",
  Together_Qwen_Qwen2_5_72B = "Qwen/Qwen2-72B-Instruct",
  Together_Qwen_Qwen2_VL_72B = "Qwen/Qwen2-VL-72B-Instruct",
  Together_mistralai_Mistral_7B_Instruct = "together/mistralai/Mistral-7B-Instruct-v0.1",
  Together_mistralai_Mistral_7B_Instruct_v0_2 = "together/mistralai/Mistral-7B-Instruct-v0.2",
  Together_mistralai_Mixtral8x7B_Instruct_46_7B = "together/mistralai/Mixtral-8x7B-Instruct-v0.1",
  Together_mistralai_Mixtral8x22B_Instruct_141B = "together/mistralai/Mixtral-8x22B-Instruct-v0.1",
  Together_NousResearch_Nous_Capybara_v1_9_7B = "together/NousResearch/Nous-Capybara-7B-V1p9",
  Together_NousResearch_Nous_Hermes_2__Mistral_DPO_7B = "together/NousResearch/Nous-Hermes-2-Mistral-7B-DPO",
  Together_NousResearch_Nous_Hermes_2__Mixtral_8x7BDPO_46_7B = "together/NousResearch/Nous-Hermes-2-Mixtral-8x7B-DPO",
  Together_NousResearch_Nous_Hermes_2__Mixtral_8x7BSFT_46_7B = "together/NousResearch/Nous-Hermes-2-Mixtral-8x7B-SFT",
  Together_NousResearch_Nous_Hermes_LLaMA2_7B = "together/NousResearch/Nous-Hermes-llama-2-7b",
  Together_NousResearch_Nous_Hermes_Llama2_13B = "together/NousResearch/Nous-Hermes-Llama2-13b",
  Together_NousResearch_Nous_Hermes2_Yi_34B = "together/NousResearch/Nous-Hermes-2-Yi-34B",
  Together_OpenChat_OpenChat_3_5_7B = "together/openchat/openchat-3.5-1210",
  Together_OpenOrca_OpenOrca_Mistral_7B_8K = "together/Open-Orca/Mistral-7B-OpenOrca",
  Together_Qwen_Qwen_1_5_Chat_0_5B = "together/Qwen/Qwen1.5-0.5B-Chat",
  Together_Qwen_Qwen_1_5_Chat_1_8B = "together/Qwen/Qwen1.5-1.8B-Chat",
  Together_Qwen_Qwen_1_5_Chat_4B = "together/Qwen/Qwen1.5-4B-Chat",
  Together_Qwen_Qwen_1_5_Chat_7B = "together/Qwen/Qwen1.5-7B-Chat",
  Together_Qwen_Qwen_1_5_Chat_14B = "together/Qwen/Qwen1.5-14B-Chat",
  Together_Qwen_Qwen_1_5_Chat_32B = "together/Qwen/Qwen1.5-32B-Chat",
  Together_Qwen_Qwen_1_5_Chat_72B = "together/Qwen/Qwen1.5-72B-Chat",
  Together_Qwen_Qwen_1_5_Chat_110B = "together/Qwen/Qwen1.5-110B-Chat",
  Together_SnorkelAI_Snorkel_Mistral_PairRM_DPO_7B = "together/snorkelai/Snorkel-Mistral-PairRM-DPO",
  Together_Snowflake_Snowflake_Arctic_Instruct = "together/Snowflake/snowflake-arctic-instruct",
  Together_Stanford_Alpaca_7B = "together/togethercomputer/alpaca-7b",
  Together_Teknium_OpenHermes2Mistral_7B = "together/teknium/OpenHermes-2-Mistral-7B",
  Together_Teknium_OpenHermes2_5Mistral_7B = "together/teknium/OpenHermes-2p5-Mistral-7B",
  Together_LLaMA27B32KInstruct_7B = "together/togethercomputer/Llama-2-7B-32K-Instruct",
  Together_RedPajamaINCITE_Chat_3B = "together/togethercomputer/RedPajama-INCITE-Chat-3B-v1",
  Together_RedPajamaINCITE_Chat_7B = "together/togethercomputer/RedPajama-INCITE-7B-Chat",
  Together_StripedHyena_Nous_7B = "together/togethercomputer/StripedHyena-Nous-7B",
  Together_Undi95_ReMM_SLERP_L2_13B = "together/Undi95/ReMM-SLERP-L2-13B",
  Together_Undi95_Toppy_M_7B = "together/Undi95/Toppy-M-7B",
  Together_WizardLM_WizardLM_v1_2_13B = "together/WizardLM/WizardLM-13B-V1.2",
  Together_upstage_Upstage_SOLAR_Instruct_v1_11B = "together/upstage/SOLAR-10.7B-Instruct-v1.0",
}

export type LLM = string | NativeLLM;

/** Equivalent to a Python enum's .name property */
export function getEnumName(
  enumObject: any,
  enumValue: any,
): string | undefined {
  for (const key in enumObject) if (enumObject[key] === enumValue) return key;
  return undefined;
}

/**
 * A list of model providers
 */
export enum LLMProvider {
  WebLLM = "webllm",
  OpenAI = "openai",
  Azure_OpenAI = "azure",
  Anthropic = "anthropic",
  Google = "google",
  HuggingFace = "hf",
  Ollama = "ollama",
  OllamaDecision = "ollama-decision",
  Bedrock = "bedrock",
  Together = "together",
  DeepSeek = "deepseek",
  MiniMax = "minimax",
  OpenRouter = "openrouter",
  Custom = "__custom",
}

/**
 * Given an LLM, return what the model provider is.
 * @param llm the specific large language model
 * @returns an `LLMProvider` describing what provider hosts the model
 */
/**
 * Whether an OpenAI model generates images (GPT Image, or the retired DALL·E),
 * and so goes through the Images API instead of chat completions. Matches by
 * name, so newer snapshots (e.g. "gpt-image-2-2026-04-21") are recognized too.
 */
export function isOpenAIImageModel(llm: LLM | string): boolean {
  const name = llm.toString().toLowerCase();
  return (
    name.startsWith("gpt-image") ||
    name.startsWith("chatgpt-image") ||
    name.startsWith("dall-e")
  );
}

/** OpenAI's reasoning effort levels, lowest first. */
export const OPENAI_REASONING_EFFORTS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

/**
 * The reasoning efforts each OpenAI model accepts, from its page in OpenAI's
 * docs (https://developers.openai.com/api/docs/models). Other values return a
 * 400. Models whose page doesn't say (e.g. gpt-5-mini, gpt-5-nano, the
 * o-series and gpt-5.6-cyber) aren't listed.
 */
const OPENAI_MODEL_REASONING_EFFORTS: Record<string, string[]> = {
  "gpt-6-astra": ["low", "medium", "high", "xhigh", "max"],
  "gpt-6.1-sol": ["low", "medium", "high", "xhigh", "max"],
  "gpt-6-sol": ["none", "low", "medium", "high", "xhigh", "max"],
  "gpt-6-luna": ["none", "low", "medium", "high", "xhigh", "max"],
  "gpt-5.6-sol": ["none", "low", "medium", "high", "xhigh", "max"],
  "gpt-5.6": ["none", "low", "medium", "high", "xhigh", "max"], // alias of gpt-5.6-sol
  "gpt-5.6-terra": ["none", "low", "medium", "high", "xhigh", "max"],
  "gpt-5.6-luna": ["none", "low", "medium", "high", "xhigh", "max"],
  "gpt-5.5": ["none", "low", "medium", "high", "xhigh"],
  "gpt-5.5-pro": ["medium", "high", "xhigh"],
  "gpt-5.4": ["none", "low", "medium", "high", "xhigh"],
  "gpt-5.4-mini": ["none", "low", "medium", "high", "xhigh"],
  "gpt-5.4-nano": ["none", "low", "medium", "high", "xhigh"],
  "gpt-5.4-pro": ["medium", "high", "xhigh"],
  "gpt-5.2": ["none", "low", "medium", "high", "xhigh"],
  "gpt-5.1": ["none", "low", "medium", "high"],
  "gpt-5": ["minimal", "low", "medium", "high"],
  "gpt-5-pro": ["high"],
};

/**
 * The reasoning efforts an OpenAI model accepts, if OpenAI's docs say.
 * Dated snapshots (e.g. "gpt-5.4-2026-03-05") take their model's.
 */
export function openAIReasoningEfforts(model: string): string[] | undefined {
  return OPENAI_MODEL_REASONING_EFFORTS[
    model.replace(/-\d{4}-\d{2}-\d{2}$/, "")
  ];
}

/**
 * A reasoning effort the model accepts: the given one if it does, or else the
 * nearest one it does (the higher, on a tie). Models OpenAI's docs don't cover
 * get the effort unchanged.
 */
export function fitOpenAIReasoningEffort(
  model: string,
  effort: string,
): string {
  const allowed = openAIReasoningEfforts(model);
  const rank = OPENAI_REASONING_EFFORTS.indexOf(effort);
  if (!allowed || allowed.includes(effort) || rank < 0) return effort;
  let best = allowed[0];
  for (const e of allowed) {
    const d = Math.abs(OPENAI_REASONING_EFFORTS.indexOf(e) - rank);
    const bestD = Math.abs(OPENAI_REASONING_EFFORTS.indexOf(best) - rank);
    if (d <= bestD) best = e;
  }
  return best;
}

/** Whether a Google model generates images (e.g. "gemini-3.1-flash-image"). */
export function isGeminiImageModel(llm: LLM | string): boolean {
  return /^(models\/)?gemini-[\w.-]*image/i.test(llm.toString());
}

/**
 * OpenRouter serves hundreds of models, and users can type in any model ID
 * (e.g. "anthropic/claude-sonnet-5"). ChainForge prefixes those IDs so they map
 * back to OpenRouter and never collide with other providers' model names.
 */
export const OPENROUTER_PREFIX = "openrouter/";
/** Prefix for OpenRouter image models, which go through its Image API instead of chat completions. */
export const OPENROUTER_IMAGE_PREFIX = "openrouter-image/";

/** Whether a model is an OpenRouter image model (e.g. "openrouter-image/black-forest-labs/flux.2-pro"). */
export function isOpenRouterImageModel(llm: LLM | string): boolean {
  return llm.toString().startsWith(OPENROUTER_IMAGE_PREFIX);
}

/**
 * Whether a model is a "decision" model: one reached through OpenRouter's
 * decisions endpoint, e.g. TypeSafe's Jev ("openrouter/~typesafe/jev-latest"),
 * or one of Ollama's (e.g. nimble), whose model name is in its settings. Such
 * models don't generate text: they answer typed questions (yes/no, one of a
 * set of categories, or a position on a scale) about a piece of text, so
 * ChainForge only uses them as judges in an LLM Scorer.
 */
export function isDecisionModel(llm: LLM | string): boolean {
  const name = llm.toString();
  return (
    name === NativeLLM.Ollama_Decision ||
    (name.startsWith(OPENROUTER_PREFIX) &&
      /^~?typesafe\//i.test(name.substring(OPENROUTER_PREFIX.length)))
  );
}

/**
 * The decision models Ollama serves through its /v1/systemone endpoint (since
 * Ollama 0.35): Bespoke Labs' nimble, and Together AI's tev1 in two sizes.
 */
export const OLLAMA_DECISION_MODELS = ["nimble", "tev1", "tev1:0.8b"];

/**
 * The most capable of the decision models pulled in Ollama, going by the
 * order of OLLAMA_DECISION_MODELS (nimble, then tev1, ...); others come last.
 */
export function bestOllamaDecisionModel(pulled: string[]): string {
  const rank = (m: string) => {
    const i = OLLAMA_DECISION_MODELS.indexOf(m.replace(/:latest$/, ""));
    return i === -1 ? OLLAMA_DECISION_MODELS.length : i;
  };
  return [...pulled].sort((a, b) => rank(a) - rank(b))[0];
}

/**
 * Whether a model pulled in Ollama (e.g. "nimble:latest", "tev1:0.8b") is one
 * of its decision models: a Nimble or Tev model, as Ollama puts it. Goes by
 * name, since Ollama's model list doesn't say.
 */
export function isOllamaDecisionModelName(name: string): boolean {
  const base = name.split(":")[0].split("/").at(-1) ?? "";
  return /^(nimble|tev\d+)([-_.]|$)/i.test(base);
}

/**
 * The base models (settings forms) whose models only make decisions. (Jev, on
 * OpenRouter, shares its form with text models, so isn't one; see
 * isDecisionMenuItem.)
 */
export const DECISION_ONLY_BASE_MODELS = new Set<string>([
  NativeLLM.Ollama_Decision,
]);

/**
 * Whether a model menu item only makes decisions: from a decision-only form
 * (e.g. Ollama's), or a decision model on a shared one (e.g. Jev, on OpenRouter).
 */
export function isDecisionMenuItem(item: LLMSpec): boolean {
  return (
    DECISION_ONLY_BASE_MODELS.has(item.base_model) ||
    isDecisionModel(item.model)
  );
}

/**
 * Whether a model menu offers a model, or a group with any model it offers:
 * menus for text generation leave out models that only make decisions, which
 * only an LLM Scorer can use.
 */
export function offeredInMenu(
  item: LLMSpec | LLMGroup,
  allowDecisionModels: boolean,
): boolean {
  return "group" in item
    ? item.items.some((i) => offeredInMenu(i, allowDecisionModels))
    : allowDecisionModels || !isDecisionMenuItem(item);
}

/**
 * Emoji for the lab behind an OpenRouter model, so models from different labs
 * are told apart at a glance. Uses ChainForge's emoji for labs it also reaches
 * directly (e.g. Claude, OpenAI, Gemini). Unknown labs get OpenRouter's own.
 */
const OPENROUTER_LAB_EMOJIS: Record<string, string> = {
  anthropic: "📚",
  openai: "🤖",
  google: "♊",
  deepseek: "🐋",
  minimax: "🔮",
  "x-ai": "✖️",
  qwen: "🐉",
  moonshotai: "🌙",
  "meta-llama": "🦙",
  typesafe: "⚖️",
  mistralai: "🌬️",
  cohere: "🖇️",
  microsoft: "🪟",
  nvidia: "🟩",
};
export const OPENROUTER_EMOJI = "🔀";
export function openRouterEmoji(llm: LLM | string): string {
  // "~" marks an alias that always points to a lab's latest model (e.g. "~typesafe/jev-latest")
  const lab = stripOpenRouterPrefix(llm)
    .split("/")[0]
    .replace(/^~/, "")
    .toLowerCase();
  return OPENROUTER_LAB_EMOJIS[lab] ?? OPENROUTER_EMOJI;
}

/** The model ID that OpenRouter expects, without ChainForge's prefix. */
export function stripOpenRouterPrefix(llm: LLM | string): string {
  const name = llm.toString();
  for (const prefix of [OPENROUTER_IMAGE_PREFIX, OPENROUTER_PREFIX])
    if (name.startsWith(prefix)) return name.substring(prefix.length);
  return name;
}

/**
 * Hugging Face serves hundreds of models through Inference Providers, and users
 * can type in any model ID listed at https://router.huggingface.co/v1/models
 * (e.g. "meta-llama/Llama-3.1-8B-Instruct"). ChainForge prefixes those IDs so
 * they map back to Hugging Face and never collide with other providers' names.
 */
export const HUGGINGFACE_PREFIX = "huggingface/";

/** The model ID Hugging Face expects, without ChainForge's prefix. */
export function stripHuggingFacePrefix(llm: LLM | string): string {
  const name = llm.toString();
  return name.startsWith(HUGGINGFACE_PREFIX)
    ? name.substring(HUGGINGFACE_PREFIX.length)
    : name;
}

/**
 * Which models Amazon Bedrock serves depends on the account's region and model
 * access, and most models since 2025 are called through a cross-region
 * inference profile rather than a bare model ID. Users therefore type the ID
 * in, and ChainForge prefixes it so it maps back to Bedrock.
 */
export const BEDROCK_PREFIX = "bedrock/";

/** Together serves hundreds of models; its IDs are prefixed the same way. */
export const TOGETHER_PREFIX = "together/";

/** The model ID Together expects, without ChainForge's prefix. */
export function stripTogetherPrefix(llm: LLM | string): string {
  const name = llm.toString();
  return name.startsWith(TOGETHER_PREFIX)
    ? name.substring(TOGETHER_PREFIX.length)
    : name;
}

/** The model or inference profile ID Bedrock expects, without ChainForge's prefix. */
export function stripBedrockPrefix(llm: LLM | string): string {
  const name = llm.toString();
  return name.startsWith(BEDROCK_PREFIX)
    ? name.substring(BEDROCK_PREFIX.length)
    : name;
}

export function getProvider(llm: LLM): LLMProvider | undefined {
  const llm_name = getEnumName(NativeLLM, llm.toString());
  if (llm_name?.startsWith("WebLLM")) return LLMProvider.WebLLM;
  else if (
    llm.toString().startsWith(OPENROUTER_PREFIX) ||
    isOpenRouterImageModel(llm)
  )
    return LLMProvider.OpenRouter;
  else if (llm_name?.startsWith("OpenAI")) return LLMProvider.OpenAI;
  else if (llm_name?.startsWith("Azure")) return LLMProvider.Azure_OpenAI;
  else if (llm_name?.startsWith("GEMINI")) return LLMProvider.Google;
  else if (
    llm.toString().startsWith(HUGGINGFACE_PREFIX) ||
    llm_name?.startsWith("HF_")
  )
    return LLMProvider.HuggingFace;
  else if (llm.toString().startsWith("claude")) return LLMProvider.Anthropic;
  else if (llm.toString() === NativeLLM.Ollama_Decision)
    return LLMProvider.OllamaDecision;
  else if (llm_name?.startsWith("Ollama")) return LLMProvider.Ollama;
  else if (llm.toString().startsWith(BEDROCK_PREFIX))
    return LLMProvider.Bedrock;
  else if (llm_name?.startsWith("Together")) return LLMProvider.Together;
  else if (llm_name?.startsWith("DeepSeek")) return LLMProvider.DeepSeek;
  else if (llm_name?.startsWith("MiniMax")) return LLMProvider.MiniMax;
  else if (llm.toString().startsWith("__custom/")) return LLMProvider.Custom;
  else if (llm.toString().toLowerCase().endsWith("-mlc"))
    return LLMProvider.WebLLM;

  return undefined;
}

/** LLM APIs often have rate limits, which control number of requests. E.g., OpenAI: https://platform.openai.com/account/rate-limits
#   For a basic organization in OpenAI, GPT3.5 is currently 3500 and GPT4 is 200 RPM (requests per minute).
#   For Anthropic evaluaton preview of Claude, can only send 1 request at a time (synchronously). See below.
#   If a model is missing from below, it means we must send and receive only 1 request at a time (synchronous).
#   The following is only a guideline, and a bit on the conservative side.  */
export const RATE_LIMIT_BY_MODEL: { [key in LLM]?: number } = {
  [NativeLLM.OpenAI_ChatGPT]: 1000, // max RPM (API requests per minute)
  [NativeLLM.OpenAI_ChatGPT_0301]: 1000,
  [NativeLLM.OpenAI_ChatGPT_0613]: 1000,
  [NativeLLM.OpenAI_ChatGPT_16k]: 1000,
  [NativeLLM.OpenAI_ChatGPT_16k_0613]: 1000,
  [NativeLLM.OpenAI_GPT4]: 500,
  [NativeLLM.OpenAI_GPT4_0314]: 500,
  [NativeLLM.OpenAI_GPT4_0613]: 500,
  [NativeLLM.OpenAI_GPT4_1106_Prev]: 500,
  [NativeLLM.OpenAI_GPT4_0125_Prev]: 500,
  [NativeLLM.OpenAI_GPT4_Turbo_Prev]: 500,
  [NativeLLM.OpenAI_GPT4_32k]: 500,
  [NativeLLM.OpenAI_GPT4_32k_0314]: 500,
  [NativeLLM.OpenAI_GPT4_32k_0613]: 500,
  [NativeLLM.OpenAI_DallE_2]: 10, // Should be 5 images per minute (1 img per every 10 seconds); here, we've been a bit lenient with it.
  [NativeLLM.OpenAI_DallE_3]: 10, // This differs per tier, see https://platform.openai.com/docs/guides/rate-limits/usage-tiers?context=tier-one
  [NativeLLM.Azure_OpenAI]: 500, // conservative
  [NativeLLM.GEMINI_v2_5_pro]: 150,
  [NativeLLM.GEMINI_v2_5_flash]: 1000,
  [NativeLLM.GEMINI_v2_5_flash_lite]: 4000,
};

export const RATE_LIMIT_BY_PROVIDER: { [key in LLMProvider]?: number } = {
  [LLMProvider.WebLLM]: 120, // In-browser inference should be serialized and conservative by default.
  [LLMProvider.OpenAI]: 1000, // Tier 3 pricing limit is 5000 per minute, across most models, we use 1000 to be safe.
  [LLMProvider.Azure_OpenAI]: 1000, // Tier 3 pricing limit is 5000 per minute, across most models, we use 1000 to be safe.
  [LLMProvider.Anthropic]: 25, // Tier 1 pricing limit is 50 per minute, across all models; we halve this, to be safe.
  [LLMProvider.Together]: 30, // Paid tier limit is 60 per minute, across all models; we halve this, to be safe.
  [LLMProvider.Google]: 1000, // RPM for Google Gemini models 1.5 is quite generous; at base it is 1000 RPM. If you are using the free version it's 15 RPM, but we can expect most CF users to be using paid (and anyway you can just re-run prompt node until satisfied).
  [LLMProvider.DeepSeek]: 1000, // DeepSeek does not constrain users atm but they might in the future. To be safe we are limiting it to 1000 queries per minute.
  [LLMProvider.MiniMax]: 1000, // MiniMax API rate limits are generous; 1000 RPM to be safe.
  [LLMProvider.OpenRouter]: 500, // OpenRouter sets no fixed limit for paid models, but the providers behind it do; 500 RPM to be safe.
  [LLMProvider.Bedrock]: 100, // Bedrock quotas are per account, region and model; a conservative floor.
};

// Max concurrent requests. Add to this to further constrain the rate limiter.
export const MAX_CONCURRENT: { [key in string | NativeLLM]?: number } = {};

// In-browser inference runs on the one GPU, so never more than one at a time.
for (const webllm_model of Object.entries(NativeLLM)
  .filter(([name]) => name.startsWith("WebLLM"))
  .map(([, id]) => id))
  MAX_CONCURRENT[webllm_model] = 1;

const DEFAULT_RATE_LIMIT = 100; // RPM for any models not listed above

/**
 * Singleton which all LLM API calls should go through to perform rate limiting via Botteneck.
 */
export class RateLimiter {
  // eslint-disable-next-line no-use-before-define
  private static instance: RateLimiter;
  private limiters: Record<LLM, Bottleneck>;

  private constructor() {
    // Initialize the singleton instance
    this.limiters = {};
  }

  /** Gets the global RateLimiter instance. Initializes it if the singleton instance does not yet exist. */
  public static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  /** Get the Bottleneck limiter for the given model. If it doesn't already exist, instantiates it dynamically. */
  private getLimiter(model: LLM, provider: LLMProvider): Bottleneck {
    // Find if there's an existing limiter for this model
    if (!(model in this.limiters)) {
      // If there isn't, make one:
      // Find the RPM. First search if the model is present in predefined rate limits; then search for pre-defined RLs by provider; then set to default.
      const rpm =
        RATE_LIMIT_BY_MODEL[model] ??
        RATE_LIMIT_BY_PROVIDER[provider ?? LLMProvider.Custom] ??
        DEFAULT_RATE_LIMIT;
      this.limiters[model] = new Bottleneck({
        reservoir: rpm, // max requests per minute
        reservoirRefreshAmount: rpm, // refresh up to max requests every minute
        reservoirRefreshInterval: 60000, // refresh every minute
        maxConcurrent: MAX_CONCURRENT[model] ?? Math.ceil(rpm / 2), // throttle max concurrent requests to half, just in case
        minTime: 20,
      }); // space out the requests by 20ms, to be safe
    }
    return this.limiters[model];
  }

  /** Throttles the API call for the given model, using Bottleneck
   * @param model The model name, as NativeLLM
   * @param func The (async) function to call when ready
   * @param should_cancel Optional. An abort function, that if true, will abort before calling func(), throwing `UserForcedPrematureExit`
   * @returns A Promise that returns with the return value of func.
   */
  public static throttle<T>(
    model: LLM,
    provider: LLMProvider,
    func: () => PromiseLike<T>,
    should_cancel?: () => boolean,
  ): Promise<T> {
    // Rate limit per model, and abort if the API request takes 3 minutes or more.
    return this.getInstance()
      .getLimiter(model, provider)
      .schedule({}, () => {
        if (should_cancel && should_cancel())
          throw new UserForcedPrematureExit();
        return func();
      });
  }
}
